import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient, dataService } from '@rti/shared';
import PdfDownloadShell from '@/components/PdfDownloadShell';
import { trackUserEvent } from '@/utils/userTracking';
import { htmlToPdfText } from '@/utils/pdfText';

const ACC_PDF_CACHE_VERSION = 'v4';

const VALID_POLYMERS = ['PE', 'PP', 'PS', 'PVC'];
const VALID_TYPES = ['preliminary', 'final'];

// ── Stat helpers (mirrored from snapshotUtils) ────────────────────────────────

function getChange(current: number | undefined, previous: number | undefined, isPercent = false, divisor = 1, decimal = 0): string {
  if (!current || !previous || current === previous) return 'Flat';
  const diff = (current - previous) / divisor;
  const formatted = decimal ? diff.toFixed(decimal) : String(Math.round(diff));
  const sign = diff > 0 ? '+' : '';
  return isPercent ? `${sign}${formatted}%` : `${sign}${formatted}`;
}

function normalizeRows(rows: any[]): any[] {
  return rows.map(row => ({
    ...row,
    date: ['date', 'Date', 'RecordDate'].map(k => row[k]).find(Boolean) || ''
  }));
}

// ── Stat extraction per polymer ───────────────────────────────────────────────

function getPEStats(row: any, prevRow: any) {
  const pct = (v: any) => Math.round((v || 0) * 100);
  return [
    { title: 'PE: Days of Inventory',  value: `${Math.round(row.pe_days || 0)} days`,    change: getChange(row.pe_days, prevRow?.pe_days),                       isPositive: false, isNegative: false },
    { title: 'PE: Demand Rate',        value: `${pct(row.pe_demand)}%`,                  change: getChange(pct(row.pe_demand), pct(prevRow?.pe_demand), true),   isPositive: false, isNegative: false },
    { title: 'PE: Export Rate',        value: `${pct(row.pe_export)}%`,                  change: getChange(pct(row.pe_export), pct(prevRow?.pe_export), true),   isPositive: false, isNegative: false },
    { title: 'PE: Operating Rate',     value: `${pct(row.pe_operating)}%`,               change: getChange(pct(row.pe_operating), pct(prevRow?.pe_operating), true), isPositive: false, isNegative: false },
    { title: 'LLDPE: Days of Inventory', value: `${Math.round(row.lldpe_days || 0)} days`, change: getChange(row.lldpe_days, prevRow?.lldpe_days),              isPositive: false, isNegative: false },
    { title: 'LLDPE: Demand Rate',     value: `${pct(row.lldpe_demand)}%`,               change: getChange(pct(row.lldpe_demand), pct(prevRow?.lldpe_demand), true), isPositive: false, isNegative: false },
    { title: 'LLDPE: Export Rate',     value: `${pct(row.lldpe_export)}%`,               change: getChange(pct(row.lldpe_export), pct(prevRow?.lldpe_export), true), isPositive: false, isNegative: false },
    { title: 'LLDPE: Operating Rate',  value: `${pct(row.lldpe_operating)}%`,            change: getChange(pct(row.lldpe_operating), pct(prevRow?.lldpe_operating), true), isPositive: false, isNegative: false },
    { title: 'HDPE: Days of Inventory', value: `${Math.round(row.hdpe_days || 0)} days`, change: getChange(row.hdpe_days, prevRow?.hdpe_days),                  isPositive: false, isNegative: false },
    { title: 'HDPE: Demand Rate',      value: `${pct(row.hdpe_demand)}%`,                change: getChange(pct(row.hdpe_demand), pct(prevRow?.hdpe_demand), true), isPositive: false, isNegative: false },
    { title: 'HDPE: Export Rate',      value: `${pct(row.hdpe_export)}%`,                change: getChange(pct(row.hdpe_export), pct(prevRow?.hdpe_export), true), isPositive: false, isNegative: false },
    { title: 'HDPE: Operating Rate',   value: `${pct(row.hdpe_operating)}%`,             change: getChange(pct(row.hdpe_operating), pct(prevRow?.hdpe_operating), true), isPositive: false, isNegative: false },
    { title: 'LDPE: Days of Inventory', value: `${Math.round(row.ldpe_days || 0)} days`, change: getChange(row.ldpe_days, prevRow?.ldpe_days),                  isPositive: false, isNegative: false },
    { title: 'LDPE: Demand Rate',      value: `${pct(row.ldpe_demand)}%`,                change: getChange(pct(row.ldpe_demand), pct(prevRow?.ldpe_demand), true), isPositive: false, isNegative: false },
    { title: 'LDPE: Export Rate',      value: `${pct(row.ldpe_export)}%`,                change: getChange(pct(row.ldpe_export), pct(prevRow?.ldpe_export), true), isPositive: false, isNegative: false },
    { title: 'LDPE: Operating Rate',   value: `${pct(row.ldpe_operating)}%`,             change: getChange(pct(row.ldpe_operating), pct(prevRow?.ldpe_operating), true), isPositive: false, isNegative: false },
  ].map(s => ({ ...s, isNegative: s.change.startsWith('-'), isPositive: s.change.startsWith('+') }));
}

function getPPStats(row: any, prevRow: any) {
  const pct = (v: any) => Math.round((v || 0) * 100);
  return [
    { title: 'Days of Inventory', value: `${Math.round(row.days_of_inventory || 0)}`, change: getChange(row.days_of_inventory, prevRow?.days_of_inventory) },
    { title: 'Demand Rate',       value: `${pct(row.demand_rates)}%`,                 change: getChange(pct(row.demand_rates), pct(prevRow?.demand_rates), true) },
    { title: 'Export Rate',       value: `${pct(row.export_rates)}%`,                 change: getChange(pct(row.export_rates), pct(prevRow?.export_rates), true) },
    { title: 'Operating Rate',    value: `${pct(row.operating_rates)}%`,              change: getChange(pct(row.operating_rates), pct(prevRow?.operating_rates), true) },
  ].map(s => ({ ...s, isNegative: s.change.startsWith('-'), isPositive: s.change.startsWith('+') }));
}

function getPSStats(rows: any[], date: string) {
  const sortedRows = [...rows].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const idx = sortedRows.findIndex(r => r.date === date);
  const row = sortedRows[idx];
  const prev = sortedRows[idx + 1] || null;
  if (!row) return [];

  const getDaysInMonth = (dateStr: string) => {
    const d = new Date(dateStr);
    return new Date(d.getFullYear(), d.getMonth() + 2, 0).getDate();
  };
  const calcSum = (prefix: string, data: any, days: number) => {
    let sum = 0;
    ['canada', 'china', 'europe', 'latin_america', 'mexico', 'other', 'pacific_rim'].forEach(r => {
      const v = data[`chart_${prefix}_${r}`] || 0;
      if (v) sum += parseFloat(v);
    });
    return sum * days;
  };

  const smData = sortedRows[idx + 1] || row;
  const days = getDaysInMonth(smData.date);
  const smSum = calcSum('sm', smData, days);
  const bzSum = calcSum('bz', smData, days);
  const bdSum = calcSum('bd', smData, days);
  let prevSmSum = 0, prevBzSum = 0, prevBdSum = 0;
  if (prev) {
    const prevData = sortedRows[idx + 2] || prev;
    const prevDays = getDaysInMonth(prevData.date);
    prevSmSum = calcSum('sm', prevData, prevDays);
    prevBzSum = calcSum('bz', prevData, prevDays);
    prevBdSum = calcSum('bd', prevData, prevDays);
  }

  const [yr, mo, dy] = date.split('-');
  const yearAgoStr = `${Number(yr) - 1}-${mo}-${dy}`;
  const yearAgoIdx = sortedRows.findIndex(r => r.date === yearAgoStr);
  let cumulative = 0;
  if (yearAgoIdx !== -1) {
    const window = sortedRows.slice(idx, yearAgoIdx + 1).reverse();
    window.forEach(r => { cumulative += Number(r.chart_ps_inventory || 0); });
  }

  const pct = (v: any) => Math.round((v || 0) * 100);
  return [
    { title: 'YOY Cumulative Inventory (MM/lbs)', value: cumulative.toLocaleString(), change: String(Math.round(row.chart_ps_inventory || 0)) },
    { title: 'Demand Rate',                       value: `${pct(row.DemandRates || row.ps_demand_rates)}%`, change: getChange(pct(row.DemandRates || row.ps_demand_rates), pct(prev?.DemandRates || prev?.ps_demand_rates), true) },
    { title: 'Operating Rate',                    value: `${pct(row.OperatingRates || row.ps_operating)}%`, change: getChange(pct(row.OperatingRates || row.ps_operating), pct(prev?.OperatingRates || prev?.ps_operating), true) },
    { title: 'SM Export Volume (MM/lbs)',          value: smSum.toFixed(2), change: getChange(smSum, prevSmSum, false, 1, 2) },
    { title: 'BZ Import Volume (MM/gals)',         value: bzSum.toFixed(2), change: getChange(bzSum, prevBzSum, false, 1, 2) },
    { title: 'BD Import Volume (MM/lbs)',          value: bdSum.toFixed(2), change: getChange(bdSum, prevBdSum, false, 1, 2) },
  ].map(s => ({ ...s, isNegative: s.change.startsWith('-'), isPositive: s.change.startsWith('+') }));
}

function getPVCStats(rows: any[], date: string) {
  const allSorted = [...rows].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const idx = allSorted.findIndex(r => r.date === date);
  const row = allSorted[idx];
  const prevRow = allSorted[idx + 1] || null;
  const lastYearRow = allSorted.find(r => {
    const d1 = new Date(date);
    const d2 = new Date(r.date);
    return d1.getFullYear() - d2.getFullYear() === 1 && d1.getMonth() === d2.getMonth();
  }) || null;
  if (!row) return [];

  const pct = (v: any) => Math.round((parseFloat(String(v || 0)) || 0) * 100);
  return [
    { title: 'Calc. Inventory (mm-lbs)', value: String(Math.round(row.inventory_monthly || 0).toLocaleString()), change: getChange(row.inventory_monthly, prevRow?.inventory_monthly, false, 1000, 2) },
    { title: 'Demand Rate',              value: `${pct(row.demand_rates)}%`,  change: getChange(pct(row.demand_rates), pct(prevRow?.demand_rates), true) },
    { title: 'Export Rate',              value: `${pct(row.export_rates)}%`,  change: getChange(pct(row.export_rates), pct(prevRow?.export_rates), true) },
    { title: 'Operating Rate',           value: `${pct(row.operating_rates)}%`, change: getChange(pct(row.operating_rates), pct(prevRow?.operating_rates), true) },
  ].map(s => ({ ...s, isNegative: s.change.startsWith('-'), isPositive: s.change.startsWith('+') }));
}

// ── Commentary extraction ─────────────────────────────────────────────────────

function getCommentarySections(polymer: string, row: any): { title: string; text: string }[] {
  switch (polymer) {
    case 'PE':
      return [
        { title: 'Cumulative Gains/Draws', text: row.pe_inv_cum_gains_draws || '' },
        { title: 'Production & Sales', text: row.pe_prod_sales || '' },
        { title: 'HDPE', text: row.hdpe || '' },
        { title: 'LDPE', text: row.ldpe || '' },
        { title: 'LLDPE', text: row.lldpe || '' },
      ].filter(s => s.text.trim());
    case 'PP':
      return [{ title: 'Supply & Demand', text: row.pp_supply_demand || '' }].filter(s => s.text.trim());
    case 'PS':
      return [
        { title: 'Cumulative Inventory', text: row.ps_inv_cum_gains_draws || '' },
        { title: 'Supply & Demand', text: row.ps_supply_demand || '' },
        { title: 'Styrene Exports', text: row.ps_styrene_exports || '' },
        { title: 'Benzene Imports', text: row.ps_benzene_imports || '' },
        { title: 'Butadiene Imports', text: row.ps_butadiene_imports || '' },
      ].filter(s => s.text.trim());
    case 'PVC':
      return [
        { title: 'Cumulative Gains/Draws', text: row.pvc_inv_cum_gains_draws || '' },
        { title: 'Production & Sales', text: row.pvc_prod_sales || '' },
        { title: 'Global Exports', text: row.pvc_exports || '' },
        { title: 'Domestic Segments', text: row.pvc_domestic_sales || '' },
      ].filter(s => s.text.trim());
    default:
      return [];
  }
}


const OFF_WHITE = '#f3f4f6';
const DARK_GREEN = '#42b200';
const CHART_PURPLE = '#854cff';
const CAP_LINE_COLOR = '#9333ea';
const RED_COLOR = '#E85b4c';
const BLUE_COLOR = '#3838FF';
const GRAY_CAP = 'rgba(204,210,195,.3)';
const REGION_COLORS = ['#42b200', '#3838FF', '#9b9b9b', '#854cff', '#d97706', '#37a2da', '#ed3f8f', '#abb1ff', '#E85b4c', '#ffa600'];

function fmtLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  return `${d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })}-${d.getUTCFullYear().toString().slice(-2)}`;
}

const CHART_AXIS = { ticks: { color: OFF_WHITE }, grid: { color: '#4A4A4A', lineWidth: 0.3 } };

function baseChartOpts(legend = true) {
  return {
    responsive: false,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: legend
        ? { labels: { color: OFF_WHITE, usePointStyle: true, pointStyle: 'circle' as const, padding: 12 } }
        : { display: false },
      datalabels: { display: false }
    },
    scales: { x: CHART_AXIS, y: CHART_AXIS }
  };
}

// Canvas default dimensions match the PDF display ratio:
// chartAreaW/chartDisplayH ≈ 105.3mm/59mm ≈ 1.784:1 → 800/448 ≈ 1.786:1
async function renderChartImage(ChartCls: any, config: any, w = 1050, h = 590): Promise<string | null> {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.style.cssText = 'position:absolute;left:-9999px;top:0;';
  document.body.appendChild(canvas);
  try {
    const opts = {
      ...config.options,
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      plugins: { ...(config.options?.plugins || {}), datalabels: { display: false } }
    };
    const inst = new ChartCls(canvas, { ...config, options: opts });
    await new Promise(r => setTimeout(r, 100));
    const url = canvas.toDataURL('image/png');
    inst.destroy();
    return url.length > 22 ? url : null;
  } catch {
    return null;
  } finally {
    document.body.removeChild(canvas);
  }
}

function filterByMonthsBack(rows: any[], selectedDate: string, monthsBack: number): any[] {
  const anchor = new Date(selectedDate);
  return rows.filter(d => {
    const cur = new Date(d.date);
    const diff =
      (anchor.getFullYear() - cur.getFullYear()) * 12 +
      (anchor.getMonth() - cur.getMonth());
    return diff >= 0 && diff <= monthsBack;
  });
}

async function buildPECharts(ChartCls: any, rows: any[], selectedDate: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const sorted = [...rows].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  if (!sorted.length) return map;

  const cumWindow = filterByMonthsBack(sorted, selectedDate, 13);
  const otherWindow = filterByMonthsBack(sorted, selectedDate, 12);
  const cumLabels = cumWindow.map(d => fmtLabel(d.date));
  const labels = otherWindow.map(d => fmtLabel(d.date));

  const peInv = cumWindow.map(d => +(d.chart_pe_inventory || 0));
  const diffs = peInv.map((v, i, a) => i === 0 ? 0 : v - a[i - 1]);
  const cumData = diffs.reduce<number[]>((acc, v) => { acc.push((acc.at(-1) ?? 0) + v); return acc; }, [0]).slice(1);

  const img1 = await renderChartImage(ChartCls, {
    type: 'line',
    data: { labels: cumLabels, datasets: [{ label: 'PE Cumulative Draws/Gains', data: cumData, borderColor: CHART_PURPLE, backgroundColor: 'rgba(133,76,255,.1)', tension: 0.2, pointRadius: 0 }] },
    options: { ...baseChartOpts(false), scales: { x: CHART_AXIS, y: { ...CHART_AXIS, grid: { color: '#4A4A4A', lineWidth: (c: any) => c.tick.value === 0 ? 2 : 0.3 } } } }
  });
  if (img1) map.set('Cumulative Gains/Draws', img1);

  const img2 = await renderChartImage(ChartCls, {
    type: 'line',
    data: { labels, datasets: [
      { label: 'Domestic Production', data: otherWindow.map(d => +(d.chart_pe_domestic_production || 0)), borderColor: DARK_GREEN, fill: false, pointRadius: 0 },
      { label: 'Total Sales', data: otherWindow.map(d => +(d.chart_pe_total_sales || 0)), borderColor: CHART_PURPLE, fill: false, pointRadius: 0 }
    ] },
    options: baseChartOpts()
  });
  if (img2) map.set('Production & Sales', img2);

  for (const [title, pfx] of [['HDPE', 'hdpe'], ['LDPE', 'ldpe'], ['LLDPE', 'lldpe']] as [string, string][]) {
    const cap = otherWindow.map(d => +(d[`chart_${pfx}_capacity`] || 0));
    const prod = otherWindow.map(d => +(d[`chart_${pfx}_production`] || 0));
    const sales = otherWindow.map(d => +(d[`chart_${pfx}_sales`] || 0));
    const img = await renderChartImage(ChartCls, {
      type: 'line',
      data: { labels, datasets: [
        { type: 'line' as const, label: 'Capacity', data: cap, borderColor: CAP_LINE_COLOR, fill: false, order: 3, pointRadius: 0 },
        { type: 'line' as const, label: 'Production', data: prod, borderColor: DARK_GREEN, order: 1, pointRadius: 0 },
        { type: 'line' as const, label: 'Sales', data: sales, borderColor: CHART_PURPLE, order: 1, pointRadius: 0 }
      ] },
      options: baseChartOpts()
    });
    if (img) map.set(title, img);
  }
  return map;
}

async function buildPSCharts(ChartCls: any, rows: any[], selectedDate: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const sorted = [...rows].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  if (!sorted.length) return map;
  const selIdx = sorted.findIndex(d => d.date === selectedDate);
  if (selIdx === -1) return map;

  const last12 = sorted.slice(Math.max(0, selIdx - 12), selIdx + 1);
  const labels12 = last12.map(d => fmtLabel(d.date));

  let running = 0;
  const cumData = last12.map((d, i) => { if (i === 0) return 0; running += Number(d.chart_ps_inventory || 0); return running; });

  const img1 = await renderChartImage(ChartCls, {
    type: 'line',
    data: { labels: labels12, datasets: [{ label: 'PS Cumulative Draws/Gains', data: cumData, borderColor: CHART_PURPLE, backgroundColor: 'rgba(133,76,255,0.1)', fill: false, tension: 0.2, pointRadius: 0 }] },
    options: { ...baseChartOpts(false), scales: { x: CHART_AXIS, y: { ...CHART_AXIS, grid: { color: '#4A4A4A', lineWidth: (c: any) => c.tick.value === 0 ? 2 : 0.3 }, beginAtZero: true } } }
  });
  if (img1) map.set('Cumulative Inventory', img1);

  const img2 = await renderChartImage(ChartCls, {
    type: 'bar',
    data: { labels: labels12, datasets: [
      { type: 'line' as const, label: 'Capacity', data: last12.map(d => +(d.chart_ps_capacity || 0)), borderColor: GRAY_CAP, fill: false, order: 3, pointRadius: 0 },
      { type: 'bar' as const, label: 'Production', data: last12.map(d => +(d.chart_ps_production || 0)), backgroundColor: DARK_GREEN, order: 1, categoryPercentage: 0.5, barPercentage: 0.8 },
      { type: 'bar' as const, label: 'Sales', data: last12.map(d => +(d.chart_ps_sales || 0)), backgroundColor: CHART_PURPLE, order: 1, categoryPercentage: 0.5, barPercentage: 0.8 }
    ] },
    options: baseChartOpts()
  });
  if (img2) map.set('Supply & Demand', img2);

  const stackStart = Math.max(0, selIdx - 1);
  const stackEnd = Math.max(0, stackStart - 3);
  const stackData = sorted.slice(stackEnd, stackStart + 1);
  const stackLabels = stackData.map(d => fmtLabel(d.date));

  for (const [title, prefix] of [['Styrene Exports', 'sm'], ['Benzene Imports', 'bz'], ['Butadiene Imports', 'bd']] as [string, string][]) {
    const fullPfx = `chart_${prefix}_`;
    const keys = Object.keys(sorted[0] || {}).filter(k => k.startsWith(fullPfx));
    const datasets = keys.map((key, idx) => {
      const label = key.replace(fullPfx, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const data = stackData.map(row => {
        const date = new Date(row.date);
        const days = new Date(date.getFullYear(), date.getMonth() + 2, 0).getDate();
        return (+(row[key] || 0)) * days;
      });
      return { label, data, backgroundColor: REGION_COLORS[idx % REGION_COLORS.length], stack: prefix };
    });
    const img = await renderChartImage(ChartCls, {
      type: 'bar',
      data: { labels: stackLabels, datasets },
      options: { ...baseChartOpts(), plugins: { ...baseChartOpts().plugins, legend: { position: 'right' as const, maxWidth: 240, labels: { color: OFF_WHITE, usePointStyle: true, pointStyle: 'circle' as const, padding: 20, font: { size: 20 }, boxWidth: 20, boxHeight: 20 } } }, scales: { x: { stacked: true, ticks: { color: OFF_WHITE, font: { size: 24 } }, grid: { color: '#4A4A4A', lineWidth: 0.3 } }, y: { stacked: true, ticks: { color: OFF_WHITE, font: { size: 24 } }, grid: { color: '#4A4A4A', lineWidth: 0.3 } } } }
    // Canvas is sized to match the PDF display aspect ratio (chartAreaW/chartDisplayH ≈ 105.3/59 ≈ 1.784:1)
    // so jsPDF addImage does not distort the image when stretching to fit the allocated area.
    }, 1050, 590);
    if (img) map.set(title, img);
  }
  return map;
}

async function buildPVCCharts(ChartCls: any, rows: any[], selectedDate: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const sortedAll = [...rows].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  if (!sortedAll.length) return map;
  const sorted = filterByMonthsBack(sortedAll, selectedDate, 12);
  if (!sorted.length) return map;
  const labels = sorted.map(d => fmtLabel(d.date));

  let running = 0;
  const cumData = sorted.map(d => { running += Number(d.inventory_change_monthly || 0) / 1000; return running; });
  const img1 = await renderChartImage(ChartCls, {
    type: 'line',
    data: { labels, datasets: [{ label: 'PVC Cumulative Draws/Gains', data: cumData, borderColor: CHART_PURPLE, backgroundColor: 'rgba(133,76,255,0.1)', fill: false, tension: 0.2, pointRadius: 0 }] },
    options: { ...baseChartOpts(false), scales: { x: CHART_AXIS, y: { ...CHART_AXIS, grid: { color: '#4A4A4A', lineWidth: (c: any) => c.tick.value === 0 ? 2 : 0.3 } } } }
  });
  if (img1) map.set('Cumulative Gains/Draws', img1);

  const img2 = await renderChartImage(ChartCls, {
    type: 'bar',
    data: { labels, datasets: [
      { type: 'line' as const, label: 'Capacity', data: sorted.map(d => +(d.capacity || 0)), borderColor: GRAY_CAP, backgroundColor: 'rgba(204,210,195,.1)', fill: true, order: 3, pointRadius: 0 },
      { type: 'line' as const, label: 'Production', data: sorted.map(d => +(d.domestic_production || 0)), borderColor: RED_COLOR, fill: false, order: 1, pointRadius: 0 },
      { type: 'bar' as const, label: 'Domestic Demand', data: sorted.map(d => +(d.domestic_demand || 0)), backgroundColor: DARK_GREEN, stack: 'stack1', order: 2 },
      { type: 'bar' as const, label: 'Exports Total', data: sorted.map(d => +(d.exports_total || 0)), backgroundColor: BLUE_COLOR, stack: 'stack1', order: 2 }
    ] },
    options: baseChartOpts()
  });
  if (img2) map.set('Production & Sales', img2);

  const exportKeys = Object.keys(sorted[0] || {}).filter(k => k.startsWith('export_'));
  const exportDatasets = exportKeys.map((key, idx) => {
    const label = key.replace(/^export_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const data = sorted.map(d => {
      const date = new Date(d.date);
      const days = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      return (+(d[key] || 0)) * days;
    });
    return { label, data, backgroundColor: REGION_COLORS[idx % REGION_COLORS.length], stack: 'region' };
  });
  const img3 = await renderChartImage(ChartCls, {
    type: 'bar',
    data: { labels, datasets: exportDatasets },
    options: { ...baseChartOpts(), scales: { x: { stacked: true, ticks: { color: OFF_WHITE }, grid: { color: '#4A4A4A', lineWidth: 0.3 } }, y: { stacked: true, ticks: { color: OFF_WHITE }, grid: { color: '#4A4A4A', lineWidth: 0.3 } } } }
  });
  if (img3) map.set('Global Exports', img3);

  const segKeys = Object.keys(sorted[0] || {}).filter(k => k.startsWith('segment_'));
  const segDatasets = segKeys.map((key, idx) => {
    let label = key.replace(/^segment_/, '').replace(/_/g, ' ');
    const dashes = (label.match(/-/g) || []).length;
    if (dashes === 1) label = label.replace('-', ' (') + ')';
    else if (dashes > 1) { const f = label.indexOf('-'); const l = label.lastIndexOf('-'); label = label.slice(0, f) + ' (' + label.slice(f + 1, l) + ')' + label.slice(l + 1); }
    label = label.replace(/\b\w/g, c => c.toUpperCase());
    const data = sorted.map(d => {
      const date = new Date(d.date);
      const days = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      return ((+(d[key] || 0)) * days) / 1000;
    });
    return { label, data, backgroundColor: REGION_COLORS[idx % REGION_COLORS.length], stack: 'segments' };
  });
  const img4 = await renderChartImage(ChartCls, {
    type: 'bar',
    data: { labels, datasets: segDatasets },
    options: { ...baseChartOpts(), scales: { x: { stacked: true, ticks: { color: OFF_WHITE }, grid: { color: '#4A4A4A', lineWidth: 0.3 } }, y: { stacked: true, ticks: { color: OFF_WHITE }, grid: { color: '#4A4A4A', lineWidth: 0.3 } } } }
  });
  if (img4) map.set('Domestic Segments', img4);

  return map;
}

const AccPdfDownload: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'generating' | 'success' | 'error' | 'unavailable'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const run = async () => {
      const polymer = searchParams.get('polymer')?.toUpperCase();
      const month = searchParams.get('month');
      const type = searchParams.get('type')?.toLowerCase() ?? 'preliminary';

      if (!polymer || !VALID_POLYMERS.includes(polymer)) {
        setStatus('error');
        setErrorMessage('Invalid polymer parameter. Expected one of: PE, PP, PS, PVC');
        return;
      }
      if (!month || month.length !== 6 || !/^\d{6}$/.test(month)) {
        setStatus('error');
        setErrorMessage('Invalid month parameter. Expected format: YYYYMM');
        return;
      }
      if (!VALID_TYPES.includes(type)) {
        setStatus('error');
        setErrorMessage('Invalid type parameter. Expected: preliminary or final');
        return;
      }

      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
      const fileName = `ResinSmart-${polymer}-SupplyDemand-${month}-${typeLabel}-${ACC_PDF_CACHE_VERSION}.pdf`;

      try {
        const check = await apiClient.checkAccPdfExists(fileName);
        if (check.exists) {
          const blob = await apiClient.downloadAccPdf(fileName);
          const url = URL.createObjectURL(blob);
          window.location.href = url;
          setStatus('success');
          await trackUserEvent('acc_download', month, { source: 'email_link', polymer, type, cache: 'hit' });
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          return;
        }
      } catch {
        // not cached, fall through to generate
      }

      setStatus('generating');

      try {
        const isFinal = type === 'final';
        let rows: any[] = [];
        if (polymer === 'PE') {
          const resp = isFinal ? await dataService.getACCPEDataFinal() : await dataService.getACCPEData();
          rows = Array.isArray(resp) ? resp : (resp as any)?.data ?? [];
        } else if (polymer === 'PP') {
          const resp = isFinal ? await dataService.getACCPPDataFinal() : await dataService.getACCPPData();
          rows = Array.isArray(resp) ? resp : (resp as any)?.data ?? [];
        } else if (polymer === 'PS') {
          const resp = isFinal ? await dataService.getACCPSDataFinal() : await dataService.getACCPSData();
          rows = Array.isArray(resp) ? resp : (resp as any)?.data ?? [];
        } else if (polymer === 'PVC') {
          const resp = isFinal ? await dataService.getACCPVCDataFinal() : await dataService.getACCPVCData();
          rows = Array.isArray(resp) ? resp : (resp as any)?.data ?? [];
        }

        rows = normalizeRows(rows.filter(r => r.date && String(r.date).trim()));

        const targetDate = rows.find(r => {
          const d = String(r.date).substring(0, 7).replace('-', '');
          return d === month;
        });

        if (!targetDate) {
          setStatus('unavailable');
          return;
        }

        const selectedDate = targetDate.date;
        const sortedDesc = [...rows].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const idx = sortedDesc.findIndex(r => r.date === selectedDate);
        const prevRow = sortedDesc[idx + 1] || null;

        let stats: any[] = [];
        if (polymer === 'PE') stats = getPEStats(targetDate, prevRow);
        else if (polymer === 'PP') stats = getPPStats(targetDate, prevRow);
        else if (polymer === 'PS') stats = getPSStats(rows, selectedDate);
        else if (polymer === 'PVC') stats = getPVCStats(rows, selectedDate);

        const commentary = getCommentarySections(polymer, targetDate);

        let logoDataUrl: string | null = null;
        try {
          const logoResp = await fetch('/logos/PrimaryRTi/White.png');
          if (logoResp.ok) {
            const logoBlob = await logoResp.blob();
            logoDataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(logoBlob);
            });
          }
        } catch {}

        let chartImages = new Map<string, string>();
        if (polymer !== 'PP') {
          const { default: ChartCls } = await import('chart.js/auto');
          if (polymer === 'PE') chartImages = await buildPECharts(ChartCls, rows, selectedDate);
          else if (polymer === 'PS') chartImages = await buildPSCharts(ChartCls, rows, selectedDate);
          else if (polymer === 'PVC') chartImages = await buildPVCCharts(ChartCls, rows, selectedDate);
        }

        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 7.5;
        const contentWidth = pageWidth - margin * 2;
        const gutter = 5;

        const bgColor: [number, number, number] = [25, 26, 31];
        const greenColor: [number, number, number] = [124, 252, 0];
        const textColor: [number, number, number] = [222, 231, 217];
        const headerRowColor: [number, number, number] = [40, 42, 50];
        const evenRowColor: [number, number, number] = [30, 32, 38];
        const oddRowColor: [number, number, number] = [27, 28, 34];
        const redColor: [number, number, number] = [255, 90, 90];
        const jetLightColor: [number, number, number] = [58, 72, 52];

        let yPos = margin;

        const drawBackground = () => {
          doc.setFillColor(...bgColor);
          doc.rect(0, 0, pageWidth, pageHeight, 'F');
        };

        const drawPageHeader = () => {
          doc.setTextColor(...greenColor);
          doc.setFontSize(23);
          doc.setFont('helvetica', 'normal');
          doc.text(`${polymer} SUPPLY/DEMAND`, margin, yPos + 6);

          const dateLabel = new Date(`${selectedDate}T00:00:00Z`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
          doc.setTextColor(...textColor);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          doc.text(dateLabel, margin, yPos + 14);
          doc.setTextColor(...greenColor);
          doc.text(`- ${typeLabel}`, margin + doc.getTextWidth(dateLabel) + 2, yPos + 14);

          if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 75, yPos + 1, 75, 16);
          yPos += 19;
          doc.setDrawColor(...greenColor);
          doc.setLineWidth(0.5);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 8;
        };

        const addNewPage = () => {
          doc.addPage();
          drawBackground();
          yPos = margin;
          drawPageHeader();
        };

        const ensureSpace = (space: number) => {
          if (yPos + space > pageHeight - margin - 12) addNewPage();
        };

        const cleanText = (raw: string): string =>
          raw
            .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
            .replace(/\u00A0/g, ' ').replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')
            .replace(/\u2022|\u25CF|\u25CB|\u25AA|\u25AB/g, '\u00b7')
            .replace(/\u2023|\u2043|\u2219/g, '-').replace(/\u2014/g, '--')
            .replace(/\u2013/g, '-').replace(/\u201C|\u201D/g, '"')
            .replace(/\u2018|\u2019/g, "'").replace(/\u2026/g, '...').replace(/\t/g, '  ');

        const splitCommentaryToLines = (text: string, width: number): string[] => {
          const lines: string[] = [];
          for (const para of cleanText(htmlToPdfText(text)).split('\n')) {
            const trimmed = para.trim();
            if (!trimmed) lines.push('');
            else lines.push(...(doc.splitTextToSize(trimmed, width) as string[]));
          }
          return lines;
        };

        drawBackground();
        drawPageHeader();

        if (stats.length > 0) {
          const dateObj = new Date(`${selectedDate}T00:00:00Z`);
          const monthLabel = dateObj.toLocaleDateString('en-US', { year: '2-digit', month: 'short', timeZone: 'UTC' });
          const prevDateObj = new Date(dateObj);
          prevDateObj.setUTCMonth(prevDateObj.getUTCMonth() - 1);
          const prevLabel = prevDateObj.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }) + '-Chg';

          if (polymer === 'PE') {
            type PivotCell = { value: string; change: string; isPositive: boolean; isNegative: boolean };
            const pivotData: Record<string, Record<string, PivotCell>> = {};
            const seenPolymers: string[] = [];
            const seenMetrics: string[] = [];

            stats.forEach((stat) => {
              const colonIdx = stat.title.indexOf(': ');
              if (colonIdx === -1) return;
              const p = stat.title.substring(0, colonIdx);
              const metric = stat.title.substring(colonIdx + 2);
              if (!seenPolymers.includes(p)) seenPolymers.push(p);
              if (!seenMetrics.includes(metric)) seenMetrics.push(metric);
              if (!pivotData[metric]) pivotData[metric] = {};
              pivotData[metric][p] = { value: stat.value, change: stat.change, isPositive: stat.isPositive, isNegative: stat.isNegative };
            });

            const colOrder = ['HDPE', 'LDPE', 'LLDPE', 'PE'].filter(p => seenPolymers.includes(p));
            const displayName = (p: string) => p === 'PE' ? 'TOTAL PE' : p;

            const metricColW = 40;
            const remainingW = contentWidth - metricColW;
            const groupW = remainingW / colOrder.length;
            const subColW = groupW / 2;
            const h1 = 6.5;
            const h2 = 6;
            const rowH = 6.5;
            const tableH = h1 + h2 + seenMetrics.length * rowH;

            ensureSpace(tableH + 6);
            const tableStartY = yPos;

            doc.setFillColor(...headerRowColor);
            doc.rect(margin, tableStartY, contentWidth, h1, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...greenColor);
            colOrder.forEach((p, gi) => {
              const gx = margin + metricColW + gi * groupW;
              doc.text(displayName(p), gx + groupW / 2, tableStartY + 4.5, { align: 'center' });
            });

            doc.setFillColor(35, 37, 45);
            doc.rect(margin, tableStartY + h1, contentWidth, h2, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...greenColor);
            colOrder.forEach((_p, gi) => {
              const gx = margin + metricColW + gi * groupW;
              doc.text(monthLabel, gx + subColW / 2, tableStartY + h1 + 4.2, { align: 'center' });
              doc.text(prevLabel, gx + subColW + subColW / 2, tableStartY + h1 + 4.2, { align: 'center' });
            });

            yPos = tableStartY + h1 + h2;

            seenMetrics.forEach((metric, mi) => {
              doc.setFillColor(...(mi % 2 === 0 ? evenRowColor : oddRowColor));
              doc.rect(margin, yPos, contentWidth, rowH, 'F');
              doc.setFontSize(9.5);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(...textColor);
              doc.text(metric, margin + 2, yPos + 4.5);

              colOrder.forEach((p, gi) => {
                const gx = margin + metricColW + gi * groupW;
                const cell = pivotData[metric]?.[p];
                doc.setTextColor(...textColor);
                doc.text(cell?.value || '\u2014', gx + subColW / 2, yPos + 4.5, { align: 'center' });
                if (cell?.isNegative) doc.setTextColor(...redColor);
                else if (cell?.isPositive) doc.setTextColor(...greenColor);
                else doc.setTextColor(...textColor);
                doc.text(cell?.change || '\u2014', gx + subColW + subColW / 2, yPos + 4.5, { align: 'center' });
              });

              yPos += rowH;
            });

            doc.setDrawColor(...jetLightColor);
            doc.setLineWidth(0.3);
            doc.rect(margin, tableStartY, contentWidth, tableH);
            doc.setLineWidth(0.2);
            doc.line(margin + metricColW, tableStartY, margin + metricColW, tableStartY + tableH);
            doc.setLineWidth(0.15);
            doc.line(margin + metricColW, tableStartY + h1, margin + contentWidth, tableStartY + h1);
            colOrder.forEach((_p, gi) => {
              const gx = margin + metricColW + gi * groupW;
              if (gi > 0) {
                doc.setLineWidth(0.2);
                doc.line(gx, tableStartY, gx, tableStartY + tableH);
              }
              doc.setLineWidth(0.1);
              doc.line(gx + subColW, tableStartY + h1, gx + subColW, tableStartY + tableH);
            });

            yPos = tableStartY + tableH + 4;
          } else {
            const metricColW = contentWidth * 0.56;
            const valueColW = contentWidth * 0.22;
            const metricX = margin;
            const valueX = margin + metricColW;
            const changeX = margin + metricColW + valueColW;
            const headerH = 8;
            const rowH = 6.5;
            const groupSize = 4;
            const numRows = stats.length;
            const tableH = headerH + numRows * rowH;

            ensureSpace(tableH + 6);
            const tableStartY = yPos;

            doc.setFillColor(...headerRowColor);
            doc.rect(margin, tableStartY, contentWidth, headerH, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...greenColor);
            doc.text('Metric', metricX + 2, tableStartY + 5.5);
            doc.text(monthLabel, valueX + 2, tableStartY + 5.5);
            doc.text(prevLabel, changeX + 2, tableStartY + 5.5);

            yPos = tableStartY + headerH;

            stats.forEach((stat, i) => {
              doc.setFillColor(...(i % 2 === 0 ? evenRowColor : oddRowColor));
              doc.rect(margin, yPos, contentWidth, rowH, 'F');
              doc.setFontSize(8.5);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(...textColor);
              doc.text(stat.title, metricX + 2, yPos + 4.5);
              doc.text(stat.value || '\u2014', valueX + 2, yPos + 4.5);
              if (stat.isNegative) doc.setTextColor(...redColor);
              else if (stat.isPositive) doc.setTextColor(...greenColor);
              else doc.setTextColor(...textColor);
              doc.text(stat.change || '\u2014', changeX + 2, yPos + 4.5);
              yPos += rowH;
              if ((i + 1) % groupSize === 0 && i < numRows - 1) {
                doc.setDrawColor(...jetLightColor);
                doc.setLineWidth(0.15);
                doc.line(margin, yPos, margin + contentWidth, yPos);
              }
            });

            doc.setDrawColor(...jetLightColor);
            doc.setLineWidth(0.3);
            doc.rect(margin, tableStartY, contentWidth, tableH);
            doc.setLineWidth(0.15);
            doc.line(valueX, tableStartY, valueX, tableStartY + tableH);
            doc.line(changeX, tableStartY, changeX, tableStartY + tableH);

            yPos = tableStartY + tableH + 4;
          }
        }

        doc.setDrawColor(...greenColor);
        doc.setLineWidth(0.4);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;

        if (polymer === 'PP') {
          for (const section of commentary) {
            if (section.title) {
              ensureSpace(14);
              doc.setTextColor(...greenColor);
              doc.setFontSize(11);
              doc.setFont('helvetica', 'bold');
              doc.text(section.title, margin, yPos);
              yPos += 7;
            }
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...textColor);
            const lines = splitCommentaryToLines(section.text, contentWidth);
            for (const line of lines) {
              ensureSpace(5);
              if (line) doc.text(line, margin, yPos);
              yPos += line ? 5 : 2;
            }
            yPos += 6;
          }
        } else {
          const chartAreaW = contentWidth * 0.54;
          const commentaryAreaW = contentWidth * 0.46 - gutter;
          const commentaryX = margin + chartAreaW + gutter;
          const chartDisplayH = 55;

          for (const section of commentary) {
            const commentaryLines = splitCommentaryToLines(section.text, commentaryAreaW);
            const titleH = section.title ? 13 : 0;
            const commentaryH = titleH + commentaryLines.reduce((h, l) => h + (l ? 4.3 : 2), 0);
            const sectionH = Math.max(chartDisplayH + 4, commentaryH) + 6;
            ensureSpace(sectionH);

            const sectionStartY = yPos;

            const chartDataUrl = chartImages.get(section.title) ?? null;

            if (chartDataUrl) {
              doc.addImage(chartDataUrl, 'PNG', margin, sectionStartY, chartAreaW, chartDisplayH + 4);
            } else {
              doc.setFillColor(...evenRowColor);
              doc.roundedRect(margin, sectionStartY, chartAreaW, chartDisplayH + 4, 2, 2, 'F');
              doc.setTextColor(100, 110, 100);
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.text('Chart available in app', margin + chartAreaW / 2, sectionStartY + (chartDisplayH + 4) / 2, { align: 'center' });
            }

            let rightY = sectionStartY;
            if (section.title) {
              doc.setTextColor(...greenColor);
              doc.setFontSize(10);
              doc.setFont('helvetica', 'bold');
              const titleLines = doc.splitTextToSize(section.title, commentaryAreaW) as string[];
              titleLines.forEach((line: string) => {
                doc.text(line, commentaryX, rightY + 5);
                rightY += 5;
              });
              rightY += 5.5;
            }

            doc.setTextColor(...textColor);
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'normal');
            for (const line of commentaryLines) {
              if (rightY > sectionStartY + sectionH) break;
              if (line) doc.text(line, commentaryX, rightY);
              rightY += line ? 4.3 : 2;
            }

            yPos = sectionStartY + sectionH;
          }
        }

        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        const footerY = pageHeight - 8;
        doc.text('This report is for the exclusive use of RTi clients and is not to be reproduced or copied to any third party.', pageWidth / 2, footerY, { align: 'center' });
        doc.text('The prices and market information presented herein are strictly the opinion of RTi and are based on knowledge collected within the industry.', pageWidth / 2, footerY + 4, { align: 'center' });

        const pdfBlob = doc.output('blob');

        try {
          await apiClient.saveAccPdf(pdfBlob, fileName);
        } catch (err) {
          console.error('Failed to save ACC PDF to cache:', err);
        }

        const url = URL.createObjectURL(pdfBlob);
        window.location.href = url;
        setStatus('success');
        await trackUserEvent('acc_download', month, { source: 'email_link', polymer, type, cache: 'miss' });
        setTimeout(() => URL.revokeObjectURL(url), 60000);

      } catch (error: any) {
        console.error('Error generating ACC PDF:', error);
        setStatus('error');
        setErrorMessage(error?.message || 'Failed to generate PDF. Please try again.');
      }
    };

    run();
  }, [searchParams]);

  return <PdfDownloadShell status={status} errorMessage={errorMessage} />;
};

export default AccPdfDownload;
